import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DocumentTemplatesService, Template } from 'gung-common';
import { ProductFileExportWrapper } from '../../../models/product-file-export';
import { ProductFileExportService } from '../../../services/product-file-export/product-file-export.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';

type ProductFileExportFieldType = 'text' | 'number' | 'textarea' | 'array' | 'boolean' | 'template';
type FilterArrayName = 'includedFilters' | 'excludedFilters';

interface ProductFileExportField {
  key: string;
  label: string;
  type: ProductFileExportFieldType;
  nested?: 'ftp';
}

interface ProductFileExportFieldGroup {
  title: string;
  fields: ProductFileExportField[];
}

function jsonValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value || !String(value).trim()) {
    return null;
  }
  try {
    JSON.parse(value);
    return null;
  } catch (e) {
    return { invalidJson: true };
  }
}

@Component({
  selector: 'lib-product-file-export-configurations',
  templateUrl: './product-file-export-configurations.component.html',
  styleUrls: ['./product-file-export-configurations.component.css'],
  standalone: false
})
export class ProductFileExportConfigurationsComponent implements OnInit {
  protected productFileExportService = inject(ProductFileExportService);
  protected documentTemplatesService = inject(DocumentTemplatesService);
  protected formBuilder = inject(FormBuilder);
  protected gungModalService = inject(GungModalService);
  protected translateService = inject(TranslateService);

  configurations: ProductFileExportWrapper[] = [];
  templates: Template[] = [];
  selectedIndex = -1;
  isNew = false;
  form: FormGroup;
  loading = false;
  saving = false;
  deleting = false;
  loadError = '';
  saveError = '';
  filterText = '';

  fieldGroups: ProductFileExportFieldGroup[] = [
    {
      title: 'PFE_GENERAL',
      fields: [
        { key: 'assortmentId', label: 'PFE_FIELD_ASSORTMENT_ID', type: 'text' },
        { key: 'flowId', label: 'PFE_FIELD_FLOW_ID', type: 'text' },
        { key: 'templateId', label: 'PFE_FIELD_TEMPLATE', type: 'template' },
        { key: 'outboundPath', label: 'PFE_FIELD_OUTBOUND_PATH', type: 'text' },
        { key: 'fileNameWithoutExtension', label: 'PFE_FIELD_FILE_NAME', type: 'text' },
        { key: 'active', label: 'PFE_FIELD_ACTIVE', type: 'boolean' }
      ]
    },
    {
      title: 'PFE_EXPORT_FORMATS',
      fields: [
        { key: 'sendAsXML', label: 'PFE_FIELD_SEND_AS_XML', type: 'boolean' },
        { key: 'sendAsCSV', label: 'PFE_FIELD_SEND_AS_CSV', type: 'boolean' },
        { key: 'sendAsTSV', label: 'PFE_FIELD_SEND_AS_TSV', type: 'boolean' },
        { key: 'sendAsXLSX', label: 'PFE_FIELD_SEND_AS_XLSX', type: 'boolean' }
      ]
    },
    {
      title: 'PFE_EMAIL',
      fields: [
        { key: 'sendByEmail', label: 'PFE_FIELD_SEND_BY_EMAIL', type: 'boolean' },
        { key: 'sendByEmailRecipients', label: 'PFE_FIELD_RECIPIENTS', type: 'array' }
      ]
    },
    {
      title: 'PFE_FTP_SECTION',
      fields: [
        { key: 'sendToFtp', label: 'PFE_FIELD_SEND_TO_FTP', type: 'boolean' },
        { key: 'ftpProtocol', label: 'PFE_FIELD_PROTOCOL', type: 'text', nested: 'ftp' },
        { key: 'ftpServer', label: 'PFE_FIELD_SERVER', type: 'text', nested: 'ftp' },
        { key: 'ftpPort', label: 'PFE_FIELD_PORT', type: 'number', nested: 'ftp' },
        { key: 'ftpUsername', label: 'PFE_FIELD_USERNAME', type: 'text', nested: 'ftp' },
        { key: 'outBoundPath', label: 'PFE_FIELD_REMOTE_OUTBOUND_PATH', type: 'text', nested: 'ftp' }
      ]
    }
  ];

  sensitiveFields: ProductFileExportField[] = [
    { key: 'ftpPassword', label: 'PFE_FIELD_FTP_PASSWORD', type: 'text', nested: 'ftp' },
    { key: 'ftpPrivateKeyPath', label: 'PFE_FIELD_FTP_PRIVATE_KEY_PATH', type: 'text', nested: 'ftp' },
    { key: 'ftpPublicKeyPath', label: 'PFE_FIELD_FTP_PUBLIC_KEY_PATH', type: 'text', nested: 'ftp' },
    { key: 'ftpPublicKeyPasswordEncoded', label: 'PFE_FIELD_FTP_PUBLIC_KEY_PASSWORD_ENCODED', type: 'text', nested: 'ftp' }
  ];

  protected readonly stringFields = this.flattenFields(['text', 'textarea', 'template']);
  protected readonly numberFields = this.flattenFields(['number']);
  protected readonly booleanFields = this.flattenFields(['boolean']);
  protected readonly arrayFields = this.flattenFields(['array']);

  /**
   * Track filter rows by the control instance (not by index). `patchFilterRows`
   * rebuilds these arrays via clear()+push() on every load/save-reload, which
   * hands back brand-new FormGroup instances. Tracking by index would make
   * Angular reuse the existing row views and only rebind them, and a reused
   * `<select>` (the alias dropdown) does not re-render its selected option when
   * its underlying control is swapped — leaving the key dropdowns visually empty
   * after a save. Tracking by identity forces fresh row views on rebuild, so the
   * post-save render matches the (already-correct) page-reload render.
   */
  protected trackByFilterControl = (_index: number, control: AbstractControl): AbstractControl => control;

  protected duplicateIdValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value || '').trim();
    if (!value) {
      return null;
    }
    const duplicate = this.configurations.some(configuration => configuration.id && configuration.id === value);
    return duplicate ? { duplicateId: true } : null;
  };

  ngOnInit(): void {
    this.form = this.buildForm();
    this.loadConfigurations();
    this.documentTemplatesService.getTemplates().subscribe(templates => (this.templates = templates || []));
  }

  get selectedConfiguration(): ProductFileExportWrapper | null {
    return this.selectedIndex >= 0 ? this.configurations[this.selectedIndex] : null;
  }

  get includedFilters(): FormArray {
    return this.form.get('includedFilters') as FormArray;
  }

  get excludedFilters(): FormArray {
    return this.form.get('excludedFilters') as FormArray;
  }

  loadConfigurations(selectId?: string): void {
    const idToSelect = selectId ?? this.selectedConfiguration?.id;
    this.loading = true;
    this.loadError = '';
    this.productFileExportService.getAll(true).subscribe({
      next: configurations => {
        this.configurations = this.sortConfigurations(configurations || []);
        const index = idToSelect ? this.configurations.findIndex(configuration => configuration.id === idToSelect) : -1;
        this.selectIndex(index >= 0 ? index : this.configurations.length ? 0 : -1);
        this.loading = false;
      },
      error: error => {
        this.loadError = this.getErrorMessage(error);
        this.loading = false;
      }
    });
  }

  newConfiguration(): void {
    if (this.isNew) {
      return;
    }
    const blank: ProductFileExportWrapper = {
      id: undefined,
      assortmentId: '',
      flowId: '',
      templateId: '',
      outboundPath: '',
      fileNameWithoutExtension: '',
      sendAsXML: false,
      sendAsCSV: false,
      sendAsTSV: false,
      sendAsXLSX: false,
      active: false,
      sendToFtp: false,
      sendByEmail: false,
      sendByEmailRecipients: [],
      includedByExtraPropertiesValue: {},
      excludedByExtraPropertiesValue: {},
      params: {},
      productFileExportFtpProperties: {}
    };
    this.configurations = [blank, ...this.configurations];
    this.selectIndex(0);
  }

  copyConfiguration(): void {
    if (!this.selectedConfiguration || this.isNew) {
      return;
    }
    const clone = this.cloneConfiguration(this.selectedConfiguration);
    clone.id = undefined;
    this.configurations = [clone, ...this.configurations];
    this.selectIndex(0);
  }

  cancelNew(): void {
    if (!this.isNew) {
      return;
    }
    this.configurations = this.configurations.filter((_, index) => index !== this.selectedIndex);
    this.selectIndex(this.configurations.length ? 0 : -1);
  }

  selectConfiguration(configuration: ProductFileExportWrapper): void {
    this.selectIndex(this.configurations.indexOf(configuration));
  }

  selectIndex(index: number): void {
    this.selectedIndex = index;
    this.saveError = '';
    if (index < 0) {
      this.isNew = false;
      this.form?.reset();
      this.form?.markAsPristine();
      return;
    }
    this.isNew = !this.configurations[index]?.id;
    this.patchForm(this.configurations[index]);
    const idControl = this.form.get('id');
    if (this.isNew) {
      idControl?.enable();
    } else {
      idControl?.disable();
    }
  }

  save(): void {
    if (!this.selectedConfiguration || this.form.invalid || this.saving) {
      return;
    }

    let params: { [key: string]: any } = {};
    const rawParams = this.form.get('paramsJson')?.value;
    if (rawParams && String(rawParams).trim()) {
      try {
        params = JSON.parse(rawParams);
      } catch (e) {
        this.saveError = this.translateService.instant('PFE_PARAMS_INVALID_JSON');
        return;
      }
    }

    const payload = this.buildPayload();
    payload.params = params;
    this.saving = true;
    this.saveError = '';

    this.productFileExportService.save(payload).subscribe({
      next: response => {
        const saved = response || payload;
        this.saving = false;
        this.isNew = false;
        this.loadConfigurations(saved.id);
        const msg = this.translateService.instant('PFE_SAVED_SUCCESS');
        this.gungModalService.openConfirmYesNoModal(undefined, msg, { size: 'sm' }, 'OK', null).then(_ => {});
      },
      error: error => {
        this.saveError = this.getErrorMessage(error);
        this.saving = false;
      }
    });
  }

  deleteConfiguration(): void {
    if (!this.selectedConfiguration?.id || this.deleting) {
      return;
    }
    const id = this.selectedConfiguration.id;
    const msg = this.translateService.instant('PFE_DELETE_CONFIRM', { id });
    this.gungModalService
      .openConfirmYesNoModal(undefined, msg, { size: 'sm' })
      .then(confirmed => {
        if (!confirmed) {
          return;
        }
        this.deleting = true;
        this.productFileExportService.delete(id).subscribe({
          next: () => {
            this.deleting = false;
            this.configurations = this.configurations.filter(configuration => configuration.id !== id);
            this.selectIndex(this.configurations.length ? 0 : -1);
          },
          error: error => {
            this.saveError = this.getErrorMessage(error);
            this.deleting = false;
          }
        });
      });
  }

  addFilterRow(arrayName: FilterArrayName): void {
    (this.form.get(arrayName) as FormArray).push(this.buildFilterFormGroup());
    this.form.markAsDirty();
  }

  removeFilterRow(arrayName: FilterArrayName, index: number): void {
    (this.form.get(arrayName) as FormArray).removeAt(index);
    this.form.markAsDirty();
  }

  onFilterTextChange(value: string): void {
    this.filterText = value || '';
  }

  isSelected(configuration: ProductFileExportWrapper): boolean {
    return this.selectedIndex >= 0 && this.configurations[this.selectedIndex] === configuration;
  }

  hasExistingSensitiveValue(key: string): boolean {
    const value = this.selectedConfiguration?.productFileExportFtpProperties?.[key];
    return value !== undefined && value !== null && value !== '';
  }

  getSensitiveControlName(key: string, suffix: 'NewValue' | 'Clear'): string {
    return `${key}${suffix}`;
  }

  trackByConfigurationId(index: number, configuration: ProductFileExportWrapper): string {
    return configuration.id || `new-${index}`;
  }

  trackByFieldKey(_index: number, field: ProductFileExportField): string {
    return field.key;
  }

  trackByIndex(index: number): number {
    return index;
  }

  get filteredConfigurations(): ProductFileExportWrapper[] {
    const filter = this.filterText.trim().toLowerCase();
    if (!filter) {
      return this.configurations;
    }

    return this.configurations.filter(configuration =>
      [configuration.id, configuration.assortmentId, configuration.templateId, configuration.outboundPath]
        .filter(value => value !== undefined && value !== null)
        .some(value => String(value).toLowerCase().includes(filter))
    );
  }

  protected buildForm(): FormGroup {
    const controls: { [s: string]: any } = {};
    controls['id'] = this.formBuilder.control('', [Validators.required, this.duplicateIdValidator]);
    this.stringFields.forEach(field => (controls[field.key] = this.formBuilder.control('')));
    this.numberFields.forEach(field => (controls[field.key] = this.formBuilder.control(null)));
    this.booleanFields.forEach(field => (controls[field.key] = this.formBuilder.control(false)));
    this.arrayFields.forEach(field => (controls[field.key] = this.formBuilder.control('')));
    this.sensitiveFields.forEach(field => {
      controls[this.getSensitiveControlName(field.key, 'NewValue')] = this.formBuilder.control('');
      controls[this.getSensitiveControlName(field.key, 'Clear')] = this.formBuilder.control(false);
    });
    controls['includedFilters'] = this.formBuilder.array([]);
    controls['excludedFilters'] = this.formBuilder.array([]);
    controls['paramsJson'] = this.formBuilder.control('', [jsonValidator]);
    return this.formBuilder.group(controls);
  }

  protected buildFilterFormGroup(key = '', values = ''): FormGroup {
    return this.formBuilder.group({ key: [key], values: [values] });
  }

  protected patchFilterRows(arrayName: FilterArrayName, map: { [key: string]: string[] } | undefined): void {
    const formArray = this.form.get(arrayName) as FormArray;
    formArray.clear();
    Object.keys(map || {}).forEach(key => formArray.push(this.buildFilterFormGroup(key, (map[key] || []).join(', '))));
  }

  protected buildFilterMap(arrayName: FilterArrayName): { [key: string]: string[] } {
    const formArray = this.form.get(arrayName) as FormArray;
    const result: { [key: string]: string[] } = {};
    formArray.controls.forEach(control => {
      const key = String(control.get('key')?.value || '').trim();
      if (!key) {
        return;
      }
      result[key] = String(control.get('values')?.value || '')
        .split(',')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);
    });
    return result;
  }

  protected patchForm(configuration: ProductFileExportWrapper): void {
    const values: { [s: string]: any } = {};
    values['id'] = configuration.id || '';
    this.stringFields.forEach(field => (values[field.key] = this.readField(configuration, field) ?? ''));
    this.numberFields.forEach(field => (values[field.key] = this.readField(configuration, field) ?? null));
    this.booleanFields.forEach(field => (values[field.key] = !!this.readField(configuration, field)));
    this.arrayFields.forEach(field => (values[field.key] = this.joinArrayValue(configuration[field.key])));
    this.sensitiveFields.forEach(field => {
      values[this.getSensitiveControlName(field.key, 'NewValue')] = '';
      values[this.getSensitiveControlName(field.key, 'Clear')] = false;
    });
    values['paramsJson'] = configuration.params && Object.keys(configuration.params).length
      ? JSON.stringify(configuration.params, null, 2)
      : '';
    this.form.reset(values);
    this.patchFilterRows('includedFilters', configuration.includedByExtraPropertiesValue);
    this.patchFilterRows('excludedFilters', configuration.excludedByExtraPropertiesValue);
    this.form.markAsPristine();
  }

  protected buildPayload(): ProductFileExportWrapper {
    const payload: ProductFileExportWrapper = this.isNew ? {} as ProductFileExportWrapper : this.cloneConfiguration(this.selectedConfiguration);
    payload.id = String(this.form.get('id')?.value || '').trim();
    payload.productFileExportFtpProperties = payload.productFileExportFtpProperties || {};

    this.stringFields.forEach(field => this.writeField(payload, field, this.form.get(field.key)?.value));
    this.numberFields.forEach(field => this.applyNumber(payload, field));
    this.booleanFields.forEach(field => this.writeField(payload, field, !!this.form.get(field.key)?.value));
    this.arrayFields.forEach(field => (payload[field.key] = this.splitArrayValue(this.form.get(field.key)?.value)));
    this.applySensitiveFields(payload);
    payload.includedByExtraPropertiesValue = this.buildFilterMap('includedFilters');
    payload.excludedByExtraPropertiesValue = this.buildFilterMap('excludedFilters');

    return payload;
  }

  protected readField(configuration: ProductFileExportWrapper, field: ProductFileExportField): any {
    if (field.nested === 'ftp') {
      return configuration.productFileExportFtpProperties?.[field.key];
    }
    return configuration[field.key];
  }

  protected writeField(payload: ProductFileExportWrapper, field: ProductFileExportField, value: any): void {
    if (field.nested === 'ftp') {
      payload.productFileExportFtpProperties[field.key] = value;
      return;
    }
    payload[field.key] = value;
  }

  protected applyNumber(payload: ProductFileExportWrapper, field: ProductFileExportField): void {
    const value = this.form.get(field.key)?.value;
    if (value === undefined || value === null || value === '') {
      this.writeField(payload, field, undefined);
      return;
    }
    const parsed = Number(value);
    this.writeField(payload, field, Number.isNaN(parsed) ? undefined : parsed);
  }

  protected applySensitiveFields(payload: ProductFileExportWrapper): void {
    this.sensitiveFields.forEach(field => {
      const newValue = this.form.get(this.getSensitiveControlName(field.key, 'NewValue'))?.value;
      const clear = !!this.form.get(this.getSensitiveControlName(field.key, 'Clear'))?.value;
      if (clear) {
        this.writeField(payload, field, undefined);
      } else if (newValue !== undefined && newValue !== null && newValue !== '') {
        this.writeField(payload, field, newValue);
      }
    });
  }

  protected flattenFields(types: ProductFileExportFieldType[]): ProductFileExportField[] {
    return this.fieldGroups.flatMap(group => group.fields.filter(field => types.includes(field.type)));
  }

  protected joinArrayValue(value: unknown): string {
    if (!Array.isArray(value)) {
      return '';
    }
    return value.join('\n');
  }

  protected splitArrayValue(value: unknown): string[] {
    if (value === undefined || value === null || value === '') {
      return [];
    }
    return String(value)
      .split(/[\n,;]+/)
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0);
  }

  protected cloneConfiguration(configuration: ProductFileExportWrapper): ProductFileExportWrapper {
    return JSON.parse(JSON.stringify(configuration));
  }

  protected sortConfigurations(configurations: ProductFileExportWrapper[]): ProductFileExportWrapper[] {
    return [...configurations].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  }

  protected getErrorMessage(error: any): string {
    const fallback = this.translateService.instant('ERROR');
    const errorBody = error?.error;
    if (typeof errorBody === 'object' && errorBody !== null) {
      return errorBody.gungMessage || errorBody.message || errorBody.error || fallback;
    }
    if (typeof errorBody === 'string' && errorBody.trim()) {
      return errorBody.split('\n')[0].trim();
    }
    return error?.message || fallback;
  }
}
